import {
  PatientInfoDrawer,
  StudyInfoDrawer,
  fetchFlaggedStudiesCount,
  fetchOngoingStudyCount,
  handleDeleteStudies,
  hasCardioOneFacilities,
  studyNavigationState,
} from '@/entities/study';
import { useSupportStore } from '@/entities/support';
import { useUserStore } from '@/entities/user';
import { DeviceInfoDrawer } from '@/entities/device';
import { facilitySelection } from '@/entities/facility';
import {
  SearchComponent as SearchComponentImport,
  CustomButton,
  CustomTabs as CustomTabsComponent,
} from '@/shared/ui/components';
import { TitleComponent } from '@/widgets/page-header';
import { EMITTER_CONSTANTS } from '@/shared/config';
import {
  auth,
  emitter,
  showMessageError,
  showMessageSuccess,
  GETTEXT_ALL_FACILITIES,
  GETTEXT_ALL_STUDIES,
} from '@/shared/lib';
import { useMergeState, useUpdateEffect, useElementChange } from '@/shared/lib/customHooks';
import classNames from 'classnames';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { STUDIES_TABS } from '../../config/studiesTabs';
import type { StudiesState } from '../../model/studiesPage.types';
import './studies.scss';
import type { FormattedStudy } from '../studiesTable/studiesTable.types';
import StudiesTableImport from '../studiesTable/studiesTable';
import ImportStudyModal from '../importStudyModal/importStudyModal';

const StudiesTable = StudiesTableImport as any;
const SearchComponent = SearchComponentImport as any;

// StudiesState declares clickedData as a required FormattedStudy, but no drawer has a study
// clicked until one is, so this page widens it to its nullable reality.
type NullableStudiesState = Omit<StudiesState, 'clickedData'> & {
  clickedData: FormattedStudy | null | undefined;
  isOpenImportStudyModal: boolean;
  activeListVersion: number;
};

const CustomTabs = CustomTabsComponent as any;

/**
 * Renders the clinic studies page with its Active, Unassigned, Completed, Flagged and Deleted tabs, the facility-scoped search bar, the New Study button and the study, device and patient information drawers. Seeds the active tab from router state or the remembered navigation tab, reads facility and patient scope from the stores, reloads the tab counts when that scope changes, tracks window scroll and banner classes on the header, and leaves the flagged tab when the permission is lost.
 *
 * @returns The studies page tree.
 */
const StudiesPage = () => {
  const { state: locationState } = useLocation();

  const navigate = useNavigate();

  /**
   * Holds the facilities the current user supports, used to gate facility pickers and study creation. Read from the support store via a useSupportStore selector on supportFacilities.
   *
   * @returns The support facilities list from the support store.
   */
  const supportFacilitiesRedux = useSupportStore((s) => s.supportFacilities);
  /**
   * Holds the facility currently selected by the user for scoping the studies lists. Read from the user store via a useUserStore selector on selectedFacility.
   *
   * @returns The selected facility entry from the user store.
   */
  const selectedFacilityRedux = useUserStore((s) => s.selectedFacility);
  /**
   * Holds the patient-visibility scope chosen by the user, deciding whether all studies or only the user's own are listed. Read from the user store via a useUserStore selector on selectedTypeSeePatients.
   *
   * @returns The selected see-patients type from the user store.
   */
  const selectedTypeSeePatients = useUserStore((s) => s.selectedTypeSeePatients);

  const hasCardioOne = hasCardioOneFacilities(supportFacilitiesRedux ?? []);

  const [state, setState] = useMergeState<NullableStudiesState>({
    activeTab: locationState?.isFlaggedStudiesTab
      ? '4'
      : locationState?.shouldNavigateToUnassignedTab
        ? '2'
        : studyNavigationState.tab || '1',
    isVisibleStudyInfoDrawer: false,
    isVisibleDeviceInfoDrawer: false,
    isVisiblePatientInfoDrawer: false,
    isVisibleCommonQuestionsDrawer: false,
    ongoingStudyCount: 0,
    ongoingStudyCountUnassigned: 0,
    ongoingStudyCountArtifact: 0,
    clickedData: undefined,
    searchByValue: locationState?.deviceId
      ? 'deviceId'
      : studyNavigationState.search.searchByValue
        ? studyNavigationState.search.searchByValue
        : 'patientName',
    searchValue: locationState?.deviceId || studyNavigationState.search.searchValue,
    studyType: 'All type',
    checkedStudyIds: [],
    singleSelectedStudyIds: [],
    isOpenConfirmDeleteStudiesModal: false,
    isDeletingStudies: false,
    multiStudyFid: studyNavigationState.search.multiStudyFid,
    fetchStudyId: null,
    isOpenImportStudyModal: false,
    activeListVersion: 0,
  });

  const isClinicTechnician = useRef(auth.isClinicTechnician() || auth.isAdminOrSales());
  const searchInputRef = useRef<{ resetState: () => void; handleFocusInput: () => void } | null>(
    null,
  );
  const onGoingStudiesRef = useRef(null);
  const headerRef = useRef<HTMLDivElement>(null);

  const isChangePosition = useElementChange(headerRef);

  /**
   * Drops the current multi-study selection on the completed studies tab. Empties checkedStudyIds in page state.
   *
   * @returns Nothing.
   */
  const onClickClearSelectStudies = () => {
    setState({ checkedStudyIds: [] });
  };

  /**
   * Records the studies tab the user last visited in the module-level study navigation state. Assigns the tab key so returning to the page reopens the same tab.
   *
   * @param tab - Key of the tab to remember.
   * @returns Nothing.
   */
  const updateTabGoto = (tab: string) => {
    _.assign(studyNavigationState, { tab });
  };

  /**
   * Switches the studies page to another tab, ignoring a request for the tab already active. Persists the tab in the study navigation state, updates activeTab in page state and scrolls the window back to the top.
   *
   * @param tab - Key of the tab to activate.
   * @returns Nothing.
   */
  const navigateTab = (tab: string) => {
    if (tab !== state.activeTab) {
      updateTabGoto(tab);
      setState({ activeTab: tab });
      window.scroll({ top: 0, left: 0 });
    }
  };

  /**
   * Starts creation of a new study, blocking facilities that are 60 days in arrears. Emits the escalation popup event with the arrears value when the user supports exactly one such facility, and otherwise navigates to the study creation route.
   *
   * @returns Nothing.
   */
  const onClickNewStudy = () => {
    if (
      supportFacilitiesRedux?.length === 1 &&
      supportFacilitiesRedux?.[0]?.arrears.toString() === '60'
    ) {
      emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, supportFacilitiesRedux?.[0]?.arrears);
    } else {
      navigate('/studies/create');
    }
  };

  /**
   * Toggles one of the studies page detail drawers and records the row that was clicked. Switches on the drawer title to flip the study, device, patient or common-questions visibility flag — the study case also storing fetchStudyId from the clicked row — and closes every drawer for any other title.
   *
   * @param clickedData - The study row the drawer should display; null when closing.
   * @param title - The drawer title selecting which drawer to toggle.
   * @returns Nothing.
   */
  const toggleDisplayDrawer = (clickedData: FormattedStudy | null = null, title = '') => {
    const setStateObject = { clickedData };

    switch (title) {
      case t`Study information`: {
        _.assign(setStateObject, {
          isVisibleStudyInfoDrawer: !state.isVisibleStudyInfoDrawer,
          fetchStudyId: clickedData?.id,
        });
        break;
      }
      case t`Device information`: {
        _.assign(setStateObject, { isVisibleDeviceInfoDrawer: !state.isVisibleDeviceInfoDrawer });
        break;
      }
      case t`Patient information`: {
        _.assign(setStateObject, { isVisiblePatientInfoDrawer: !state.isVisiblePatientInfoDrawer });
        break;
      }
      case t`Common Questions`: {
        _.assign(setStateObject, {
          isVisibleCommonQuestionsDrawer: !state.isVisibleCommonQuestionsDrawer,
        });
        break;
      }
      default: {
        _.assign(setStateObject, {
          isVisibleStudyInfoDrawer: false,
          isVisibleDeviceInfoDrawer: false,
          isVisiblePatientInfoDrawer: false,
          isVisibleCommonQuestionsDrawer: false,
        });
        break;
      }
    }

    setState(setStateObject);
  };

  /**
   * Collects the facility ids allowed to view flagged studies, used to scope the flagged-studies count query. Reduces the user's support facilities to the ids whose canViewFlaggedStudies flag is set only when All Facilities is selected in local storage, and returns undefined for a single selected facility since its id already scopes the query.
   *
   * @returns The permitted facility ids, or undefined when a single facility is selected.
   */
  const getCanViewFlaggedStudiesFacilityIds = () => {
    const userSupportFacilities = facilitySelection.userSupportFacilities();
    const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
    if (selectedFacilityLocalStorage.value === GETTEXT_ALL_FACILITIES) {
      return _.reduce(
        userSupportFacilities,
        (canViewFlaggedStudiesFacilityIds, supportFacility) => {
          if (supportFacility.canViewFlaggedStudies) {
            canViewFlaggedStudiesFacilityIds.push(supportFacility.id);
          }
          return canViewFlaggedStudiesFacilityIds;
        },
        [] as string[],
      );
    }
    return undefined;
  };

  /**
   * Loads the badge counts shown on the studies tabs. Builds a query filter from the selected facility and the see-all-studies scope, requests the ongoing and unassigned ongoing counts in parallel, adds the flagged-studies count for clinic technicians scoped to the permitted facility ids, writes the three counts to state and logs plus surfaces an error message on failure.
   *
   * @returns A promise resolving once the counts are stored or the error is reported.
   */
  const handleFetchOngoingCount = async () => {
    try {
      const queryFilter = {
        facilityId:
          selectedFacilityRedux.value !== GETTEXT_ALL_FACILITIES
            ? selectedFacilityRedux.value
            : undefined,
        viewAll: selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES,
      };
      const promises = [
        fetchOngoingStudyCount(queryFilter),
        fetchOngoingStudyCount({ ...queryFilter, isUnassigned: true }),
      ];
      if (isClinicTechnician.current) {
        const canViewFlaggedStudiesFacilityIds = getCanViewFlaggedStudiesFacilityIds();
        const flaggedFilter = {
          facilityIds: queryFilter?.facilityId
            ? [queryFilter?.facilityId]
            : canViewFlaggedStudiesFacilityIds,
        };
        promises.push(fetchFlaggedStudiesCount(flaggedFilter).then((count) => count ?? 0));
      }
      const [ongoingStudyCount, ongoingStudyCountUnassigned, ongoingStudyCountArtifact] =
        await Promise.all(promises);
      setState({
        ongoingStudyCount,
        ongoingStudyCountUnassigned,
        ongoingStudyCountArtifact: ongoingStudyCountArtifact || 0,
      });
    } catch (error) {
      console.error('Failed to fetch ongoing count: ', error);
      showMessageError({ content: (error as any)?.message });
    }
  };

  /**
   * Reloads the Active tab after a CardioOne study was imported. Remounts the Active studies table by bumping activeListVersion, so it fetches its list again, and reloads the tab counts.
   *
   * @returns Nothing.
   */
  const onImportedStudy = () => {
    setState({ activeListVersion: state.activeListVersion + 1 });
    handleFetchOngoingCount();
  };

  /**
   * Applies a change from the studies search bar to both page state and the module-level study navigation state so the choice survives navigation. A searchValue change stores the new term, while a change of the search-by field stores the field and clears the current term.
   *
   * @param name - Which search control changed: searchValue or the search-by field.
   * @param value - The new value of that control.
   * @returns Nothing.
   */
  const onChangeSearchComponent = (name: string, value: string) => {
    if (name === 'searchValue') {
      _.assign(studyNavigationState, {
        search: {
          ...studyNavigationState.search,
          searchValue: value,
        },
      });
      setState({ searchValue: value });
    } else {
      _.assign(studyNavigationState, {
        search: {
          ...studyNavigationState.search,
          searchByValue: value,
        },
      });
      setState({
        searchByValue: value,
        searchValue: '',
      });
    }
  };

  /**
   * Deletes the studies confirmed in the delete modal and reports the outcome. Sends the single selected id when the modal was opened from a row action and then drops that id from the checkbox selection, otherwise sends every checked id and pluralises the success message; both paths close the modal and clear the deleting flag, and a failure logs the error and shows it as a message.
   *
   * @returns A promise resolving once the deletion result has been applied to state.
   */
  const deleteStudies = async () => {
    try {
      setState({ isDeletingStudies: true });

      if (state.singleSelectedStudyIds.length > 0) {
        await handleDeleteStudies(state.singleSelectedStudyIds);
        showMessageSuccess({
          content: t`Deleted study successfully`,
        });
        /**
         * Holds the remaining checked study ids after the single study just deleted is excluded. Built by filtering state.checkedStudyIds against the first entry of state.singleSelectedStudyIds.
         *
         * @returns Checked study ids without the deleted study id.
         */
        const updatedArray = _.filter(
          state.checkedStudyIds,
          (item) => item !== state.singleSelectedStudyIds[0],
        );
        setState({
          singleSelectedStudyIds: [],
          checkedStudyIds: updatedArray,
          isOpenConfirmDeleteStudiesModal: false,
          isDeletingStudies: false,
        });
      } else {
        await handleDeleteStudies(state.checkedStudyIds);
        const studyText = state.checkedStudyIds.length > 1 ? 'studies' : 'study';
        showMessageSuccess({
          content: t`Deleted ${studyText} successfully`,
        });
        // *: Delete studies via socket io
        setState({
          checkedStudyIds: [],
          isOpenConfirmDeleteStudiesModal: false,
          isDeletingStudies: false,
        });
      }
    } catch (error) {
      console.error('Failed to delete studies', error);
      showMessageError({ content: (error as any)?.message });
      setState({ isOpenConfirmDeleteStudiesModal: false, isDeletingStudies: false });
    }
  };

  /**
   * Applies a single row's checkbox toggle to the multi-study selection. Pushes the row id onto a copy of checkedStudyIds when checked and removes it when unchecked, then writes the result back to page state.
   *
   * @param checked - Whether the row is being selected or deselected.
   * @param row - The study row that was toggled.
   * @returns Nothing.
   */
  const onSelectStudy = (checked: boolean, row: FormattedStudy) => {
    const newCheckedStudyIds = [...state.checkedStudyIds];
    if (checked) {
      newCheckedStudyIds.push(row.id);
    } else {
      _.remove(newCheckedStudyIds, (x) => x === row.id);
    }
    setState({ checkedStudyIds: newCheckedStudyIds });
  };

  /**
   * Applies a select-all or deselect-all toggle over the study rows currently rendered. Pushes every row id onto a copy of checkedStudyIds when checked and removes those ids when unchecked, then writes the result back to page state.
   *
   * @param checked - Whether the rows are being selected or deselected.
   * @param rows - The study rows covered by the toggle.
   * @returns Nothing.
   */
  const onSelectAllStudies = (checked: boolean, rows: FormattedStudy[]) => {
    const newCheckedStudyIds = [...state.checkedStudyIds];
    if (checked) {
      _.forEach(rows, (row) => {
        newCheckedStudyIds.push(row.id);
      });
    } else {
      _.forEach(rows, (row) => {
        _.remove(newCheckedStudyIds, (x) => x === row.id);
      });
    }
    setState({ checkedStudyIds: newCheckedStudyIds });
  };

  /**
   * Opens or closes the delete-studies confirmation modal, keeping single-row and multi-row deletions in separate state buckets. A single-id request stores the ids as singleSelectedStudyIds and clears the checkbox selection, while a bulk request stores them as checkedStudyIds and clears the single bucket.
   *
   * @param checkedStudyIds - The study ids the confirmation applies to.
   * @param isOpenConfirmDeleteStudiesModal - Whether the confirmation modal should be open.
   * @param isSingleId - Whether the request comes from a single row action rather than the bulk selection.
   * @returns Nothing.
   */
  const toggleDisplayConfirmDeleteStudiesModal = (
    checkedStudyIds: string[],
    isOpenConfirmDeleteStudiesModal: boolean,
    isSingleId: boolean,
  ) => {
    if (isSingleId) {
      setState({
        singleSelectedStudyIds: checkedStudyIds,
        checkedStudyIds: [],
        isOpenConfirmDeleteStudiesModal,
      });
    } else {
      setState({
        singleSelectedStudyIds: [],
        checkedStudyIds,
        isOpenConfirmDeleteStudiesModal,
      });
    }
  };

  /**
   * Clears the studies search after the user dismisses a no-result state. Resets the multi study FID list and search value in page state and calls resetState on the search input ref.
   *
   * @returns Nothing.
   */
  const handleClickClear = () => {
    setState({
      multiStudyFid: [],
      searchValue: '',
    });
    searchInputRef.current?.resetState();
  };

  /**
   * Restarts the studies search after a failed lookup by returning focus to the input. Focuses the search input through its ref and clears the multi study FID list and search value in page state.
   *
   * @returns Nothing.
   */
  const handleClickTry = () => {
    searchInputRef.current?.handleFocusInput();
    setState({
      multiStudyFid: [],
      searchValue: '',
    });
  };

  /**
   * Keeps the studies page header in sync with the window scroll position so it can render a sticky shadow. Adds the is-scrolling class to the header element once scrollY leaves the top and removes it when scrollY returns to zero.
   *
   * @returns Nothing.
   */
  const handleScroll = () => {
    if (headerRef.current) {
      const { classList } = headerRef.current;
      if (window.scrollY === 0 && classList.contains('is-scrolling')) {
        headerRef.current.classList.remove('is-scrolling');
      } else if (window.scrollY !== 0 && !classList.contains('is-scrolling')) {
        headerRef.current.classList.add('is-scrolling');
      }
    }
  };

  /**
   * Decides whether the Flagged studies tab may be shown for the user's current facility scope. When a single facility is selected in local storage it reports that facility's canViewFlaggedStudies flag from the user's support facilities, and when All Facilities is selected it reports whether any support facility grants the permission.
   *
   * @returns True when flagged studies are viewable for the current facility scope.
   */
  const checkFacilityCanViewFlaggedStudies = () => {
    const userSupportFacilities = facilitySelection.userSupportFacilities();
    const selectedFacilityLocalStorage = facilitySelection.selectedFacilityLocalStorage();
    if (selectedFacilityLocalStorage?.value !== GETTEXT_ALL_FACILITIES) {
      /**
       * Holds the support facility whose id matches the facility value stored in local storage. Resolved with a lodash find over the user's support facilities, and is undefined when no facility matches.
       *
       * @returns The matching support facility object, or undefined.
       */
      const foundSelectedFacility = _.find(
        userSupportFacilities,
        (x) => x.id === selectedFacilityLocalStorage.value,
      );
      return foundSelectedFacility?.canViewFlaggedStudies;
    }
    return !!_.find(
      userSupportFacilities,
      (supportFacility) => supportFacility.canViewFlaggedStudies,
    );
  };

  /**
   * Builds the tab descriptors of the studies page: Active, Unassigned, Completed and Deleted, each rendering a studies table configured for that tab. The Active, Unassigned and Deleted tabs carry their badge counts and refresh callback, the Completed tab additionally wires multi-selection and study deletion, and a Flagged studies tab is inserted for clinic technicians whose facility scope allows viewing flagged studies.
   *
   * @returns Ordered tab items for the CustomTabs component.
   */
  const listTabStudy = () => {
    const arrayTab = [
      {
        key: '1',
        label: <div>{t`Active`}</div>,
        children: (
          <StudiesTable
            key={state.activeListVersion}
            ref={onGoingStudiesRef}
            isActive={state.activeTab === '1'}
            isOngoing
            type={STUDIES_TABS.ONGOING}
            toggleDisplayDrawer={toggleDisplayDrawer}
            handleFetchOngoingCount={handleFetchOngoingCount}
            searchValue={state.searchValue}
            searchByValue={state.searchByValue}
            studyType={state.studyType}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
            multiStudyFid={state.multiStudyFid}
            handleClickTry={handleClickTry}
            handleClickClear={handleClickClear}
            supportFacilities={supportFacilitiesRedux}
          />
        ),
        quantity: state.ongoingStudyCount || undefined,
      },
      {
        key: '2',
        label: <div>{t`Unassigned`}</div>,
        children: (
          <StudiesTable
            isActive={state.activeTab === '2'}
            isUnassigned
            type={STUDIES_TABS.UNASSIGNED}
            toggleDisplayDrawer={toggleDisplayDrawer}
            handleFetchOngoingCount={handleFetchOngoingCount}
            searchValue={state.searchValue}
            searchByValue={state.searchByValue}
            studyType={state.studyType}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
            multiStudyFid={state.multiStudyFid}
            handleClickTry={handleClickTry}
            handleClickClear={handleClickClear}
            supportFacilities={supportFacilitiesRedux}
          />
        ),
        quantity: state.ongoingStudyCountUnassigned || undefined,
      },
      {
        key: '3',
        label: <div>{t`Completed`}</div>,
        children: (
          <StudiesTable
            isActive={state.activeTab === '3'}
            isCompleted
            type={STUDIES_TABS.COMPLETED}
            toggleDisplayDrawer={toggleDisplayDrawer}
            searchValue={state.searchValue}
            searchByValue={state.searchByValue}
            studyType={state.studyType}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
            checkedStudyIds={state.checkedStudyIds}
            onSelectStudy={onSelectStudy}
            onSelectAllStudies={onSelectAllStudies}
            isOpenConfirmDeleteStudiesModal={state.isOpenConfirmDeleteStudiesModal}
            toggleDisplayConfirmDeleteStudiesModal={toggleDisplayConfirmDeleteStudiesModal}
            deleteStudies={deleteStudies}
            isDeletingStudies={state.isDeletingStudies}
            multiStudyFid={state.multiStudyFid}
            handleClickTry={handleClickTry}
            handleClickClear={handleClickClear}
            supportFacilities={supportFacilitiesRedux}
            onClickClearSelectStudies={onClickClearSelectStudies}
          />
        ),
      },
    ];
    if (isClinicTechnician.current && checkFacilityCanViewFlaggedStudies()) {
      arrayTab.push({
        key: '4',
        label: <div>{t`Flagged studies`}</div>,
        children: (
          <StudiesTable
            isActive={state.activeTab === '4'}
            isFlaggedStudiesTab
            type={STUDIES_TABS.ONGOING}
            toggleDisplayDrawer={toggleDisplayDrawer}
            handleFetchOngoingCount={handleFetchOngoingCount}
            searchValue={state.searchValue}
            searchByValue={state.searchByValue}
            studyType={state.studyType}
            selectedFacility={selectedFacilityRedux}
            selectedTypeSeePatients={selectedTypeSeePatients}
            multiStudyFid={state.multiStudyFid}
            supportFacilities={supportFacilitiesRedux}
            handleClickClear={handleClickClear}
          />
        ),
        quantity: state.ongoingStudyCountArtifact || undefined,
      });
    }
    arrayTab.push({
      key: '5',
      label: <div>{t`Deleted`}</div>,
      children: (
        <StudiesTable
          isActive={state.activeTab === '5'}
          isDeleted
          type={STUDIES_TABS.DELETED}
          toggleDisplayDrawer={toggleDisplayDrawer}
          searchValue={state.searchValue}
          searchByValue={state.searchByValue}
          studyType={state.studyType}
          selectedFacility={selectedFacilityRedux}
          selectedTypeSeePatients={selectedTypeSeePatients}
          multiStudyFid={state.multiStudyFid}
          supportFacilities={supportFacilitiesRedux}
          handleClickClear={handleClickClear}
        />
      ),
    });
    return arrayTab;
  };

  useEffect(() => {
    document.title = t`Biocare Diagnostics - Studies`;
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    handleFetchOngoingCount();
    if (!studyNavigationState.tab) {
      updateTabGoto('1');
    }
  }, []);

  useEffect(() => {
    if (locationState?.isFlaggedStudiesTab) {
      updateTabGoto('4');
      setState({ activeTab: '4' });
    } else if (locationState?.shouldNavigateToUnassignedTab) {
      updateTabGoto('2');
      setState({ activeTab: '2' });
    }
  }, [locationState]);

  useEffect(() => {
    if (headerRef.current) {
      if (auth.isShowLoginByBanner()) {
        headerRef.current.classList.add('has-login-banner');
      } else {
        headerRef.current.classList.remove('has-login-banner');
      }
    }
  }, [headerRef.current, isChangePosition]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [headerRef.current]);

  useUpdateEffect(() => {
    setState({ checkedStudyIds: [] });
    if (state.activeTab === '4' && !checkFacilityCanViewFlaggedStudies()) {
      navigateTab('1');
    }
    handleFetchOngoingCount();
  }, [selectedFacilityRedux, selectedTypeSeePatients]);

  return (
    <>
      <StudyInfoDrawer
        visible={state.isVisibleStudyInfoDrawer}
        fetchStudyId={state.fetchStudyId ?? undefined}
        handleClickCloseButton={() => {
          toggleDisplayDrawer();
        }}
        isOngoing={state.activeTab === '1'}
        isFlaggedStudiesTab={state.activeTab === '4'}
      />
      <DeviceInfoDrawer
        visible={state.isVisibleDeviceInfoDrawer}
        deviceId={state.clickedData?.device?.deviceId}
        handleClickCloseButton={() => {
          toggleDisplayDrawer();
        }}
      />
      <PatientInfoDrawer
        visible={state.isVisiblePatientInfoDrawer}
        title={t`Patient information`}
        studyId={state.clickedData?.id}
        handleClickCloseButton={() => {
          toggleDisplayDrawer();
        }}
        isDeletedStudy={state.activeTab === '5'}
      />

      <ImportStudyModal
        isOpen={state.isOpenImportStudyModal}
        onClose={() => setState({ isOpenImportStudyModal: false })}
        onImported={onImportedStudy}
      />

      <div
        ref={headerRef}
        className={classNames(
          'custom-page studies-page animated fadeIn --is-clinic',
          {
            'do-not-has-banner':
              (!auth.hasLogoBanner() && !auth.hasMarketingBanner()) || auth.hasLogoBanner(),
          },
          { 'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner() },
        )}
      >
        <div className="top-container">
          <TitleComponent
            title={t`Studies`}
            isShowListFacility={
              facilitySelection.userSupportFacilities()?.length > 0 ||
              (supportFacilitiesRedux?.length ?? 0) > 0
            }
            isShowTypeSeeStudies
          />
        </div>
        <div className="top-nav-tabs-container">
          <CustomTabs
            className="studies-tab"
            tabTitleClassName="tab-title"
            activeKey={state.activeTab}
            onChange={navigateTab}
            items={listTabStudy() as any}
          />
          <div className="search-bar">
            {(facilitySelection.userSupportFacilities()?.length > 0 ||
              (supportFacilitiesRedux?.length ?? 0) > 0) && (
              <SearchComponent
                ref={searchInputRef}
                searchValue={state.searchValue}
                searchByValue={state.searchByValue}
                onChange={onChangeSearchComponent}
                inputType={
                  ['studyFid', 'deviceId'].includes(state.searchByValue) ? 'number' : 'text'
                }
                showSearch
              />
            )}
            {hasCardioOne ? (
              <CustomButton
                size="small"
                type="primary"
                onClick={() => setState({ isOpenImportStudyModal: true })}
              >
                {t`Import Study`}
              </CustomButton>
            ) : (
              <CustomButton size="small" type="primary" onClick={onClickNewStudy}>
                {t`New Study`}
              </CustomButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudiesPage;
